/**
 * Canonical tract-level values and reference statistics for GenericMapTiered.
 * KPI cards, legend reference, and statewide break arrays must use these functions.
 */

import { normalizeCensusTractGeoid } from './countyFipsMapping';
import type { MapVariableId } from './genericMapClassification';
import { extractNumericValuesForVariable } from './genericMapClassification';
import {
  computeReferenceFromValues,
  MAP_REFERENCE_DEFINITIONS,
  meanOf,
  medianOf,
  type MapReferenceDefinition,
} from './mapReferenceStatistics';

export type TractValueGetField = (id: MapVariableId) => string;

/** Supabase select fragments — must include every column needed for derivation. */
export const STATEWIDE_FETCH_SELECT: Record<string, string> = {
  ev_by_tract_2023: [
    'tract',
    'evs_per_100_people',
    'total_chargers',
    'vehicles_per_household',
    'pct_zero_vehicle_households',
    'total_population',
    'avg_travel_time',
    'persons_per_sqmi',
    'dem_minus_rep',
    'pct_owner_occupied',
    'pct_bachelor_or_higher',
  ].join(','),
  cbei_tracts:
    'geofips,transportation_total_emissions,housing_total_emissions,food_total_emissions,goods_total_emissions,services_total_emissions',
};

export function tractValuesFromRows(
  rows: any[],
  variableId: MapVariableId,
  getField: TractValueGetField
): number[] {
  return extractNumericValuesForVariable(rows, variableId, getField);
}

export type JurisdictionSummary = {
  currentValue: number;
  jurisdictionLabel: string;
  minValue?: number;
  maxValue?: number;
};

export type MapMetricsScope = 'county' | 'region' | 'california';

export type MapScopeLabelContext = {
  countyName?: string;
  regionName?: string;
};

export function tractGeoidFromRow(row: Record<string, unknown>): string | null {
  const raw = row.tract ?? row.geofips;
  if (raw == null) return null;
  return normalizeCensusTractGeoid(String(raw));
}

export function filterTractRowsByGeoids(rows: any[], geoids: Set<string>): any[] {
  if (!geoids.size) return [];
  return rows.filter((row) => {
    const geoid = tractGeoidFromRow(row);
    return geoid != null && geoids.has(geoid);
  });
}

export function locationKpiLabel(locationName: string, variableId: MapVariableId): string {
  if (variableId === 'income') return `Median per tract (${locationName})`;
  if (variableId === 'political_lean') return `Pop.-weighted mean (${locationName})`;
  return `Mean per tract (${locationName})`;
}

export function peerKpiLabelForScope(scope: MapMetricsScope, ctx: MapScopeLabelContext): string {
  if (scope === 'california') return 'California mean per tract (2023)';
  if (scope === 'region' && ctx.regionName) return `${ctx.regionName} mean per tract (2023)`;
  const county = (ctx.countyName ?? 'County').replace(/ County$/i, '');
  return `${county} County mean per tract (2023)`;
}

export function rowsForSelectedLocation(
  loadedRows: any[],
  locationType: 'city' | 'county' | 'state' | 'unknown',
  placeTractGeoids: Set<string> | null
): any[] {
  if (locationType === 'city' && placeTractGeoids && placeTractGeoids.size > 0) {
    const filtered = filterTractRowsByGeoids(loadedRows, placeTractGeoids);
    if (filtered.length > 0) return filtered;
  }
  return loadedRows;
}

export function summarizeJurisdiction(
  rows: any[],
  variableId: MapVariableId,
  getField: TractValueGetField,
  populationWeightedPoliticalMean?: (rows: any[], field: string) => number | null,
  options?: { jurisdictionLabel?: string }
): JurisdictionSummary {
  const values = tractValuesFromRows(rows, variableId, getField);
  let minValue: number | undefined;
  let maxValue: number | undefined;
  if (values.length > 0) {
    minValue = Math.min(...values);
    maxValue = Math.max(...values);
  }

  const defaultLoadedLabel =
    variableId === 'income'
      ? 'Median per tract (loaded area)'
      : variableId === 'political_lean'
        ? 'Pop.-weighted mean (loaded area)'
        : 'Mean per tract (loaded area)';
  const jurisdictionLabel = options?.jurisdictionLabel ?? defaultLoadedLabel;

  if (variableId === 'income') {
    return {
      currentValue: medianOf(values) ?? 0,
      jurisdictionLabel,
      minValue,
      maxValue,
    };
  }

  if (variableId === 'political_lean' && populationWeightedPoliticalMean) {
    const field = getField('political_lean');
    return {
      currentValue: populationWeightedPoliticalMean(rows, field) ?? meanOf(values) ?? 0,
      jurisdictionLabel,
      minValue,
      maxValue,
    };
  }

  return {
    currentValue: meanOf(values) ?? 0,
    jurisdictionLabel,
    minValue,
    maxValue,
  };
}

export type StatewideReference = {
  value: number;
  label: string;
  statistic: MapReferenceDefinition['statistic'];
  tractCount: number;
};

/** Peer geography for map KPI + legend (county / region / California for current data scope). */
export function summarizeMapScopePeer(
  loadedRows: any[],
  variableId: MapVariableId,
  scope: MapMetricsScope,
  scopeLabels: MapScopeLabelContext,
  getField: TractValueGetField,
  populationWeightedPoliticalMean?: (rows: any[], field: string) => number | null
): StatewideReference {
  const peerLabel = peerKpiLabelForScope(scope, scopeLabels);
  const summary = summarizeJurisdiction(
    loadedRows,
    variableId,
    getField,
    populationWeightedPoliticalMean,
    { jurisdictionLabel: peerLabel }
  );
  const def = MAP_REFERENCE_DEFINITIONS[variableId];
  return {
    value: summary.currentValue,
    label: peerLabel,
    statistic: def?.statistic ?? 'mean_per_tract',
    tractCount: loadedRows.length,
  };
}

export function statewideReferenceFromValues(
  variableId: MapVariableId,
  statewideValues: number[] | null | undefined
): StatewideReference {
  const def = MAP_REFERENCE_DEFINITIONS[variableId];
  const tractCount = statewideValues?.length ?? 0;
  const computed =
    statewideValues && statewideValues.length > 0
      ? computeReferenceFromValues(variableId, statewideValues)
      : null;

  if (computed != null && Number.isFinite(computed)) {
    const liveLabel =
      def.statistic === 'median_per_tract'
        ? 'California median per tract (2023)'
        : def.statistic === 'population_weighted_mean'
          ? 'California pop.-weighted mean (2023)'
          : 'California mean per tract (2023)';
    return { value: computed, label: liveLabel, statistic: def.statistic, tractCount };
  }

  return {
    value: def.staticValue,
    label: def.staticLabel,
    statistic: def.statistic,
    tractCount: 0,
  };
}

/** Human-readable reference for legend + KPI (same numeric value). */
export function formatMapReferenceValue(
  variableId: MapVariableId,
  value: number,
  options?: { unit?: string; title?: string }
): string {
  const unit = options?.unit ?? '';
  const title = options?.title ?? '';

  if (title === 'Political Lean') {
    if (value > 0) return `D +${value.toFixed(1)}`;
    if (value < 0) return `R +${Math.abs(value).toFixed(1)}`;
    return 'Even';
  }

  switch (variableId) {
    case 'commute_time':
      return `${value.toFixed(1)} min`;
    case 'ev_adoption':
      return `${value.toFixed(1)} EVs/100`;
    case 'total_evs':
      return value < 10 ? `${value.toFixed(1)} EVs/tract` : `${Math.round(value).toLocaleString()} EVs/tract`;
    case 'vehicles_per_household':
      return `${value.toFixed(2)} vehicles/HH`;
    case 'total_chargers':
      return `${value.toFixed(1)} chargers/tract`;
    case 'zero_vehicle_households':
      return value < 10 ? `${value.toFixed(1)} HHs/tract` : `${Math.round(value).toLocaleString()} HHs/tract`;
    case 'carbon_footprint':
      return `${value.toFixed(1)} MT CO₂e/HH`;
    case 'income':
      return `$${Math.round(value).toLocaleString()}`;
    case 'owner_occupied_housing':
    case 'educational_attainment':
      return `${value.toFixed(1)}%`;
    case 'population_density':
      return `${Math.round(value).toLocaleString()} per sq mi`;
    default:
      if (unit === '$') return `$${Math.round(value).toLocaleString()}`;
      if (unit === ' min') return `${value.toFixed(1)} min`;
      if (unit === '%') return `${value.toFixed(1)}%`;
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${unit}`;
  }
}
